import React, { useState, useEffect, useRef } from 'react';
import { X, Paperclip, PlayCircle, File as FileIcon, ChevronRight, Users } from 'react-feather';
import { XIcon } from './XIcon';
import { useAuth } from '../contexts/AuthContext';
import { userAPI, familyAPI } from '../services/api';
import { integrationsAPI } from '../services/integrationsAPI';
import { mediaUploadService } from '../services/mediaUploadService';
import { useToast } from '../contexts/ToastContext';
import Modal, { ModalHandle } from './Modal';
import ErrorState from './ErrorState';
import YouTubeVideoPickerModal from './YouTubeVideoPickerModal';
import XTagListPickerModal from './XTagListPickerModal';
import { useResponsive } from '../hooks/useResponsive';
import { getVideoDurationSeconds, xVideoDurationError } from '../utils/videoDuration';
import { TextArea } from './ui';

interface TweetCoachModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Coach fields are OMITTED for a standalone "Post to X" (KGH-785). When absent
   *  the composer opens with no coach card, no prefill, and no coach mention. */
  coachName?: string;
  twitterHandle?: string;
  coachId?: string;
  schoolId?: string;
  /** Render inside the unified CoachContactModal shell (no own overlay). */
  embedded?: boolean;
}

interface MediaFile {
  file: File;
  preview: string;
  type: 'image' | 'video' | 'document';
}

const TweetCoachModal: React.FC<TweetCoachModalProps> = ({
  isOpen,
  onClose,
  coachName,
  twitterHandle,
  coachId,
  schoolId,
  embedded = false,
}) => {
  const { sessionToken } = useAuth();
  const toast = useToast();
  const { isMobile } = useResponsive();
  const [athleteId, setAthleteId] = useState<string>('');
  const [isConnected, setIsConnected] = useState(false);
  const [isCheckingConnection, setIsCheckingConnection] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [isPosting, setIsPosting] = useState(false);
  const [tweetText, setTweetText] = useState('');
  const [attachedMedia, setAttachedMedia] = useState<MediaFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [youtubePickerVisible, setYoutubePickerVisible] = useState(false);
  const [tagListPickerVisible, setTagListPickerVisible] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Coach-anchored vs. standalone "Post to X". Everything coach-specific (the card,
  // the prefill, the mention metadata, the "Tag Coach" copy) hangs off this.
  const hasCoach = !!(coachName || twitterHandle);

  // Effective X post limit for the connected account (280 basic / 25000 Premium),
  // resolved from the integration service. Seeded at the basic limit so an
  // unresolved lookup can never let a basic account overrun 280 (#190, KGH-785).
  const [characterLimit, setCharacterLimit] = useState(280);
  // X Premium accounts may post longer videos; drives the duration check at pick time.
  const [isPremium, setIsPremium] = useState(false);
  const characterCount = tweetText.length;

  // Normalize X handle
  const normalizeTwitterHandle = (handle?: string): string => {
    if (!handle) return '';
    let normalized = handle.trim().replace(/\s+/g, '').replace(/@+/g, '');
    if (normalized) {
      normalized = `@${normalized}`;
    }
    return normalized;
  };

  // Resolve the athlete and check the X connection in one pass. Mirrors
  // DMCoachModal: the `finally` guarantees the "Checking connection…" flag is
  // always cleared, even when athlete resolution fails (#183).
  const loadAthleteAndConnect = async () => {
    if (!sessionToken) return;

    setIsCheckingConnection(true);
    setLoadError(false);
    try {
      const userProfile = await userAPI.getProfile(sessionToken);
      const familyResult = await familyAPI.getMyFamily(sessionToken);

      let resolvedAthleteId = '';
      if (familyResult.family?.members) {
        const familyMember = familyResult.family.members.find(
          member => member.id === userProfile.id
        );
        if (familyMember?.athlete_profile_id) {
          resolvedAthleteId = familyMember.athlete_profile_id;
        }
      }

      if (!resolvedAthleteId) {
        throw new Error('Could not find your athlete profile.');
      }

      setAthleteId(resolvedAthleteId);
      const connectionStatus = await integrationsAPI.getConnectionStatus(resolvedAthleteId);
      setIsConnected(connectionStatus.twitter);
      // Premium/PremiumPlus accounts get 25000 chars — resolve the real limit so
      // the composer doesn't cap a paid X account at 280. Fail-safe stays 280.
      if (connectionStatus.twitter) {
        integrationsAPI.getXCharacterLimit(resolvedAthleteId)
          .then(info => {
            setCharacterLimit(info.character_limit);
            setIsPremium(info.is_premium);
          })
          .catch(() => {
            setCharacterLimit(280);
            setIsPremium(false);
          });
      }
    } catch (error) {
      console.error('Error loading athlete data for tweet:', error);
      setIsConnected(false);
      setLoadError(true);
    } finally {
      setIsCheckingConnection(false);
    }
  };

  // Load athlete + connection and prefill the tweet once per open.
  useEffect(() => {
    if (isOpen && sessionToken) {
      const cleanHandle = normalizeTwitterHandle(twitterHandle).replace('@', '');
      setTweetText(cleanHandle ? `Hey @${cleanHandle}! ` : '');
      loadAthleteAndConnect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, sessionToken]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    // Snapshot synchronously — the input is reset below, after async checks.
    const picked = Array.from(files);

    const newMediaFiles: MediaFile[] = [];
    
    for (const file of picked) {
      // Check if we've reached the limit
      if (attachedMedia.length + newMediaFiles.length >= 4) {
        toast.warning('Maximum 4 files allowed');
        break;
      }

      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        toast.warning(`${file.name} is too large. Maximum file size is 10MB.`);
        continue;
      }

      // Determine file type
      let type: 'image' | 'video' | 'document' = 'document';
      if (file.type.startsWith('image/')) {
        type = 'image';
      } else if (file.type.startsWith('video/')) {
        type = 'video';
      }

      // X rejects over-length videos only AFTER the upload, with no usable
      // error (KGH-785). Check here, where the duration is readable.
      if (type === 'video') {
        const duration = await getVideoDurationSeconds(file);
        const tooLong = duration != null ? xVideoDurationError(duration, isPremium) : null;
        if (tooLong) {
          toast.warning(tooLong);
          continue;
        }
      }

      // Create preview URL
      const preview = URL.createObjectURL(file);

      newMediaFiles.push({ file, preview, type });
    }

    // X accepts either ONE video (with nothing else) or up to FOUR images.
    const combined = [...attachedMedia, ...newMediaFiles];
    const firstVideo = combined.find((m) => m.type === 'video');
    if (firstVideo && combined.length > 1) {
      toast.warning('X allows a single video with no other media, or up to 4 images. Keeping the first video.');
      combined.filter((m) => m !== firstVideo).forEach((m) => URL.revokeObjectURL(m.preview));
      setAttachedMedia([firstVideo]);
    } else {
      setAttachedMedia(combined);
    }
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleYouTubeVideosSelected = (videoLinks: string[]) => {
    const videosText = videoLinks.join('\n');
    const newText = tweetText ? `${tweetText}\n\n${videosText}` : videosText;
    setTweetText(newText);
  };

  // Append a saved tag list's @handles to the post. Skips any handle already in
  // the text, and warns (but still applies, so they can trim) if it overruns 280.
  const applyTagList = (handles: string[]) => {
    if (!handles || handles.length === 0) return;
    setTweetText((prev) => {
      const lower = prev.toLowerCase();
      const toAdd = handles
        .filter((h) => !lower.includes(`@${h.toLowerCase()}`))
        .map((h) => `@${h}`);
      if (toAdd.length === 0) return prev;
      const sep = prev && !prev.endsWith(' ') && !prev.endsWith('\n') ? ' ' : '';
      const next = `${prev}${sep}${toAdd.join(' ')}`;
      if (next.length > characterLimit) {
        toast.warning(`That tag list puts your post over ${characterLimit} characters — trim it before posting.`);
      }
      return next;
    });
  };

  const removeMediaFile = (index: number) => {
    const newMedia = [...attachedMedia];
    URL.revokeObjectURL(newMedia[index].preview); // Clean up preview URL
    newMedia.splice(index, 1);
    setAttachedMedia(newMedia);
  };

  const uploadMediaFiles = async (): Promise<string[]> => {
    if (attachedMedia.length === 0) return [];

    if (!athleteId || !sessionToken) {
      throw new Error('Missing athlete ID or session token');
    }

    setIsUploading(true);

    try {
      const uploadResults = await mediaUploadService.uploadMultipleMedia(
        attachedMedia,
        athleteId,
        sessionToken,
        (index, progress) => {
          console.log(`📊 Upload progress for file ${index + 1}: ${progress.percentage}%`);
          // Calculate overall progress across all files
          const overallProgress = Math.round(
            ((index + (progress.percentage / 100)) / attachedMedia.length) * 100
          );
          setUploadProgress(overallProgress);
        }
      );

      // Check if all uploads succeeded
      const failedUploads = uploadResults.filter(result => !result.success);
      if (failedUploads.length > 0) {
        console.error('❌ Some files failed to upload:', failedUploads);
        throw new Error('Some files failed to upload');
      }

      // Extract file URLs
      const mediaUrls = uploadResults
        .filter(result => result.success && result.fileUrl)
        .map(result => result.fileUrl!);

      console.log('✅ All files uploaded successfully:', mediaUrls);
      return mediaUrls;

    } catch (error) {
      console.error('Error uploading media:', error);
      throw error;
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handlePostTweet = async () => {
    if (!athleteId || !tweetText.trim()) return;

    setIsPosting(true);

    try {
      let mediaUrls: string[] = [];

      // Upload media files if any
      if (attachedMedia.length > 0) {
        mediaUrls = await uploadMediaFiles();
      }

      // Post tweet with media URLs
      await integrationsAPI.postToTwitter(athleteId, {
        text: tweetText.trim(),
        media_urls: mediaUrls.length > 0 ? mediaUrls : undefined,
        mentions: (coachId || schoolId) ? {
          coach_ids: coachId ? [coachId] : undefined,
          school_ids: schoolId ? [schoolId] : undefined
        } : undefined
      });

      toast.success('Posted to X.');
      handleClose();
    } catch (error) {
      console.error('Error posting tweet:', error);
      const message = error instanceof Error ? error.message : 'Failed to post tweet. Please try again.';
      toast.error(message);
    } finally {
      setIsPosting(false);
    }
  };

  // Route this composer's own X/Cancel through the Modal's guarded close so the
  // "Discard changes?" prompt fires from them too (not just backdrop/Escape).
  const composerModalRef = useRef<ModalHandle>(null);
  const handleClose = () => {
    setTweetText('');
    setIsPosting(false);
    // Clean up preview URLs
    attachedMedia.forEach(media => URL.revokeObjectURL(media.preview));
    setAttachedMedia([]);
    setUploadProgress(0);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
        `}
      </style>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        ref={composerModalRef}
        inline={embedded}
        isDirty={tweetText.trim().length > 0}
        disableClose={isPosting || isUploading || youtubePickerVisible || tagListPickerVisible}
        ariaLabel={hasCoach ? 'Tag Coach on X' : 'Post to X'}
        zIndex={1001}
        contentStyle={{
          background: 'white',
          borderRadius: '16px',
          width: isMobile ? 'calc(100vw - 32px)' : '100%',
          maxWidth: isMobile ? 'calc(100vw - 32px)' : '600px',
          maxHeight: isMobile ? 'calc(100vh - 100px)' : '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          padding: 0,
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '24px',
            borderBottom: '1px solid #E5E7EB',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: '#151133',
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: '20px',
              fontWeight: '700',
              color: 'white',
            }}
          >
            {hasCoach ? 'Tag Coach on X' : 'Post to X'}
          </h2>
          <button
            onClick={() => (composerModalRef.current ? composerModalRef.current.requestClose() : handleClose())}
            disabled={isPosting || isUploading}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: (isPosting || isUploading) ? 'not-allowed' : 'pointer',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '8px',
              transition: 'background 0.2s',
              opacity: (isPosting || isUploading) ? 0.5 : 1,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            <X size={24} color="white" />
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '24px', background: '#F9FAFB' }}>
          {/* Coach Info — only for a coach-anchored tweet */}
          {hasCoach && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              padding: '20px',
              background: 'white',
              borderRadius: '12px',
              marginBottom: '24px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
            }}
          >
            <div
              style={{
                width: '50px',
                height: '50px',
                borderRadius: '25px',
                background: '#F3F4F6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <XIcon size={24} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#000000', marginBottom: '4px' }}>
                {coachName}
              </div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#000000' }}>
                {normalizeTwitterHandle(twitterHandle)}
              </div>
            </div>
          </div>
          )}

          {isCheckingConnection ? (
            /* Checking Connection Status */
            <div
              style={{
                textAlign: 'center',
                padding: '60px 32px',
                background: 'white',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
              }}
            >
              <div
                style={{
                  width: '60px',
                  height: '60px',
                  background: '#F3F4F6',
                  borderRadius: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px',
                  animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                }}
              >
                <XIcon size={32} />
              </div>
              <p style={{ fontSize: '16px', color: '#6B7280', fontWeight: '600', margin: 0 }}>
                Checking X connection...
              </p>
            </div>
          ) : loadError ? (
            /* Failed to load account */
            <ErrorState
              message="We couldn't load your account to post this tweet. Please try again."
              onRetry={loadAthleteAndConnect}
              style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)' }}
            />
          ) : !isConnected ? (
            /* X Connection Required */
            <div
              style={{
                textAlign: 'center',
                padding: '40px 32px',
                background: 'white',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
              }}
            >
              <div
                style={{
                  width: '80px',
                  height: '80px',
                  background: '#F3F4F6',
                  borderRadius: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px',
                }}
              >
                <XIcon size={48} />
              </div>
              <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#000000', marginBottom: '12px', margin: '0 0 12px 0' }}>
                X Account Not Connected
              </h3>
              <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '16px', lineHeight: '1.5', margin: '0 0 16px 0' }}>
                {hasCoach
                  ? 'To tag coaches and post tweets, you need to connect your X account first.'
                  : 'To post to X from APS, you need to connect your X account first.'}
              </p>
              <p style={{ fontSize: '14px', color: '#F9550D', fontWeight: '600', lineHeight: '1.5', margin: 0 }}>
                Go to Messages <ChevronRight size={13} style={{ display: 'inline', verticalAlign: 'middle' }} aria-hidden="true" /> Settings to connect your social media accounts.
              </p>
            </div>
          ) : (
            /* Tweet Composer */
            <div
              style={{
                background: 'white',
                padding: '20px',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
              }}
            >
              <label
                style={{
                  display: 'block',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#1F2937',
                  marginBottom: '12px',
                }}
              >
                Compose your post:
              </label>
              <div
                style={{
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  marginBottom: '16px',
                }}
              >
                {/* The composer shell owns the border and holds the character
                    counter below the box, so the field inside stays borderless —
                    the primitive is here for the disabled, placeholder and focus
                    states, and for dropping the inline `outline: none` that left
                    a keyboard user with only the parent's border swap. */}
                <TextArea
                  value={tweetText}
                  onChange={(e) => setTweetText(e.target.value)}
                  placeholder="What would you like to say?"
                  aria-label="Post text"
                  maxLength={characterLimit}
                  style={{
                    minHeight: '120px',
                    maxHeight: '200px',
                    padding: '16px',
                    border: 'none',
                    borderRadius: 0,
                    fontSize: '16px',
                    resize: 'none',
                  }}
                  onFocus={(e) => {
                    e.currentTarget.parentElement!.style.borderColor = '#000000';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.parentElement!.style.borderColor = '#E5E7EB';
                  }}
                />
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    padding: '0 16px 12px 16px',
                  }}
                >
                  <div
                    style={{
                      fontSize: '12px',
                      fontWeight: '600',
                      color: characterCount > characterLimit - 20 ? '#EF4444' : '#6B7280',
                    }}
                  >
                    {characterCount}/{characterLimit}
                  </div>
                </div>
              </div>

              {/* Add a Tag List — drop your saved @handles into the post (KGH-785) */}
              <button
                onClick={() => setTagListPickerVisible(true)}
                disabled={isPosting || isUploading}
                style={{
                  width: '100%',
                  padding: '16px 20px',
                  background: '#EEF2FF',
                  border: '2px solid #151133',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#151133',
                  cursor: isPosting || isUploading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  marginBottom: '8px',
                  opacity: isPosting || isUploading ? 0.5 : 1,
                  transition: 'background 0.2s',
                }}
                onMouseEnter={(e) => {
                  if (!isPosting && !isUploading) e.currentTarget.style.background = '#E0E7FF';
                }}
                onMouseLeave={(e) => {
                  if (!isPosting && !isUploading) e.currentTarget.style.background = '#EEF2FF';
                }}
              >
                <Users size={20} color="#151133" />
                Add a Tag List
              </button>

              {/* YouTube Button */}
              <button
                onClick={() => {
                  setYoutubePickerVisible(true);
                }}
                disabled={isPosting || isUploading}
                style={{
                  width: '100%',
                  padding: '16px 20px',
                  background: '#FFF5F5',
                  border: '2px solid #FF0000',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#FF0000',
                  cursor: isPosting || isUploading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  marginBottom: '8px',
                  opacity: isPosting || isUploading ? 0.5 : 1,
                  transition: 'background 0.2s',
                }}
                onMouseEnter={(e) => {
                  if (!isPosting && !isUploading) e.currentTarget.style.background = '#FFEBEB';
                }}
                onMouseLeave={(e) => {
                  if (!isPosting && !isUploading) e.currentTarget.style.background = '#FFF5F5';
                }}
              >
                <PlayCircle size={20} color="#FF0000" />
                YouTube Videos
              </button>

              {/* Media Section */}
              <div style={{ marginTop: '8px' }}>
                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*,.pdf,.doc,.docx"
                  multiple
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />

                {/* Add File Button */}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={attachedMedia.length >= 4 || isPosting || isUploading}
                  style={{
                    width: '100%',
                    padding: '16px',
                    background: '#F8FAFC',
                    border: '2px dashed #F9550D',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#F9550D',
                    cursor: attachedMedia.length >= 4 || isPosting || isUploading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    opacity: attachedMedia.length >= 4 || isPosting || isUploading ? 0.5 : 1,
                    transition: 'background 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    if (attachedMedia.length < 4 && !isPosting && !isUploading) {
                      e.currentTarget.style.background = '#FFF5F0';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (attachedMedia.length < 4 && !isPosting && !isUploading) {
                      e.currentTarget.style.background = '#F8FAFC';
                    }
                  }}
                >
                  <Paperclip size={16} />
                  Add File{attachedMedia.length > 0 ? ` (${attachedMedia.length}/4)` : ''}
                </button>

                {/* Attachment Guidelines */}
                <div style={{ fontSize: '12px', color: '#9CA3AF', textAlign: 'center', marginTop: '8px', lineHeight: '1.4' }}>
                  Maximum file size: 10MB.{'\n'}
                  Supported formats: PDF, DOC, images, and more.
                </div>

                {/* Media Previews */}
                {attachedMedia.length > 0 && (
                  <div style={{ display: 'flex', gap: '12px', marginTop: '16px', overflowX: 'auto', paddingBottom: '8px' }}>
                    {attachedMedia.map((media, index) => (
                      <div
                        key={index}
                        style={{
                          position: 'relative',
                          flexShrink: 0,
                        }}
                      >
                        {media.type === 'document' ? (
                          <div
                            style={{
                              width: '100px',
                              height: '100px',
                              borderRadius: '8px',
                              background: '#F3F4F6',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: '8px',
                            }}
                          >
                            <FileIcon size={36} color="#6B7280" />
                            <div style={{ fontSize: '10px', color: '#374151', textAlign: 'center', marginTop: '4px', wordBreak: 'break-word' }}>
                              {media.file.name.length > 15 
                                ? media.file.name.substring(0, 12) + '...' 
                                : media.file.name}
                            </div>
                          </div>
                        ) : (
                          <div style={{ position: 'relative' }}>
                            <img
                              src={media.preview}
                              alt="Preview"
                              style={{
                                width: '100px',
                                height: '100px',
                                objectFit: 'cover',
                                borderRadius: '8px',
                                background: '#F3F4F6',
                              }}
                            />
                            {media.type === 'video' && (
                              <div
                                style={{
                                  position: 'absolute',
                                  bottom: '8px',
                                  left: '8px',
                                  background: 'rgba(0, 0, 0, 0.6)',
                                  borderRadius: '20px',
                                  padding: '4px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <PlayCircle size={20} color="white" />
                              </div>
                            )}
                          </div>
                        )}

                        {/* Upload Progress */}
                        {isUploading && (
                          <div
                            style={{
                              position: 'absolute',
                              inset: 0,
                              background: 'rgba(0, 0, 0, 0.7)',
                              borderRadius: '8px',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '4px',
                            }}
                          >
                            <div style={{ fontSize: '12px', color: 'white', fontWeight: '600' }}>
                              {uploadProgress}%
                            </div>
                            <div style={{ fontSize: '10px', color: 'white', opacity: 0.8 }}>
                              {uploadProgress < 50 ? 'Compressing...' : 'Uploading...'}
                            </div>
                          </div>
                        )}

                        {/* Remove Button */}
                        <button
                          onClick={() => removeMediaFile(index)}
                          disabled={isPosting || isUploading}
                          style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            background: 'rgba(239, 68, 68, 0.9)',
                            border: '1px solid white',
                            borderRadius: '12px',
                            width: '24px',
                            height: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: isPosting || isUploading ? 'not-allowed' : 'pointer',
                            opacity: isPosting || isUploading ? 0.3 : 1,
                          }}
                        >
                          <X size={14} color="white" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions - Only show when X is connected */}
        {isConnected && (
          <div
            style={{
              padding: '20px 24px',
              borderTop: '1px solid #E5E7EB',
              display: 'flex',
              gap: '12px',
              background: 'white',
            }}
          >
            <button
              onClick={() => (composerModalRef.current ? composerModalRef.current.requestClose() : handleClose())}
              disabled={isPosting || isUploading}
              style={{
                flex: 1,
                padding: '14px',
                background: 'white',
                border: '2px solid #D1D5DB',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                color: '#374151',
                cursor: isPosting || isUploading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                opacity: isPosting || isUploading ? 0.5 : 1,
              }}
              onMouseEnter={(e) => {
                if (!isPosting && !isUploading) e.currentTarget.style.background = '#F9FAFB';
              }}
              onMouseLeave={(e) => {
                if (!isPosting && !isUploading) e.currentTarget.style.background = 'white';
              }}
            >
              Cancel
            </button>
            <button
              onClick={handlePostTweet}
              disabled={isPosting || isUploading || !tweetText.trim() || characterCount > characterLimit}
              style={{
                flex: 1,
                padding: '14px',
                background: isPosting || isUploading || !tweetText.trim() || characterCount > characterLimit ? '#9CA3AF' : '#000000',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                color: 'white',
                cursor: isPosting || isUploading || !tweetText.trim() || characterCount > characterLimit ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'background 0.2s',
              }}
              onMouseEnter={(e) => {
                if (!isPosting && !isUploading && tweetText.trim() && characterCount <= characterLimit) {
                  e.currentTarget.style.background = '#0C8DD9';
                }
              }}
              onMouseLeave={(e) => {
                if (!isPosting && !isUploading && tweetText.trim() && characterCount <= characterLimit) {
                  e.currentTarget.style.background = '#000000';
                }
              }}
            >
              <XIcon size={16} />
              {isUploading ? 'Uploading...' : isPosting ? 'Posting...' : 'Post Tweet'}
            </button>
          </div>
        )}
      </Modal>

      {/* YouTube Video Picker Modal */}
      <YouTubeVideoPickerModal
        isOpen={youtubePickerVisible}
        onClose={() => setYoutubePickerVisible(false)}
        onSelectVideos={handleYouTubeVideosSelected}
        title="Select Videos"
      />

      {/* Tag List Picker — insert a saved list's @handles into the post */}
      <XTagListPickerModal
        isOpen={tagListPickerVisible}
        onClose={() => setTagListPickerVisible(false)}
        onApply={applyTagList}
      />
    </>
  );
};

export default TweetCoachModal;
